import { FiltersChips } from 'common/components/Filters/FiltersChips';
import { useTranslation } from 'react-i18next';
import { SESSION_STATUS } from '../../../utils/sessionStatus';
import { SimInventoryFilters, SimInventoryFiltersV2 } from 'simInventory/models/SimProfile.model';

import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import {
  accountsFilterAtom,
  productCodesFilterAtom,
  customFieldsFilterAtom,
  mobileNetworkOperatorFilterAtom,
  mnoNamesFilterAtom,
  SIM_FILTERS_INITIAL_VALUES,
  simFiltersAtom,
  mnoStatusFilterAtom,
  tagsFilterAtom,
  subscriptionStatusFilterAtom,
  mobileNetworkOperatorIdFilterAtom,
} from 'atoms/filters/sim';
import { TagsChips } from 'tags/Components/TagsChips';
import { AccountChips } from 'common/components/FilterChip/AccountChips';
import { ProductCodesChips } from 'common/components/FilterChip/ProductCodesChips';
import { MnosChips } from 'common/components/FilterChip/MnosChips';
import { CustomFieldsChips } from 'common/components/FilterChip/CustomFields/CustomFieldsChips';
import { MnoStatusChips } from 'common/components/FilterChip/MNOStatusChips';
import { SubscriptionStatusChips } from 'src/common/components/FilterChip/SubscriptionStatusChips';
import { createChipsContentText } from 'src/common/components/Chips/utils/chipsContentUtils';
import { omit } from 'lodash';

export const SimInventoryFilterChips = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useAtom(simFiltersAtom);

  const otherFilters = omit(filters, [
    'tags',
    'mobileNetworkOperator',
    'accounts',
    'customFields',
    'mnoStatus',
    'productCodes',
    'subscriptionStatus',
    'mobileNetworkOperatorId',
  ]);

  const clearAll = useResetAtom(simFiltersAtom);

  const onFilterClear = (filterName: any | 'iccidRange') => {
    if (filterName === 'iccidRange') {
      setFilters((currentFilters: SimInventoryFiltersV2) => ({
        ...currentFilters,
        iccidFrom: '',
        iccidTo: '',
      }));
      return;
    }

    setFilters((currentFilters: SimInventoryFiltersV2) => ({
      ...currentFilters,
      //@ts-expect-error -- filterName: any can't index SIM_FILTERS
      [filterName]: SIM_FILTERS_INITIAL_VALUES[filterName],
    }));
  };

  const createValuesForChips = (
    filters: Omit<
      SimInventoryFilters,
      'tags' | 'mobileNetworkOperator' | 'accounts' | 'customFields' | 'mnoStatus'
    >,
  ) => {
    const filtersNames = {
      sessionStatus: t('simInventory.sessionStatus'),
      accounts: t('common.account'),
      mobileNetworkOperator: t('simInventory.mobileNetworkOperator'),
      label: t('common.label'),
      orderNumber: t('common.orderNumber'),
      connectionId: t('common.connectionId'),
      tags: 'tags',
      iccidRange: '',
      mnoStatus: t('common.mnoStatus'),
    };

    const { sessionStatus, iccidFrom, iccidTo, ...othersFilters } = filters;
    const chipsValues: Partial<Record<keyof typeof filtersNames, string>> = {};

    if (sessionStatus !== SESSION_STATUS.UNKNOWN) {
      chipsValues.sessionStatus = createChipsContentText(
        filtersNames.sessionStatus,
        sessionStatus === SESSION_STATUS.IN_SESSION
          ? t('common.inSession')
          : t('common.outSession'),
      );
    }

    if (iccidFrom !== '' || iccidTo !== '') {
      chipsValues.iccidRange = `ICCID`;
      if (iccidFrom) {
        chipsValues.iccidRange += ` ${t('common.from')}: ${iccidFrom}`;
      }

      if (iccidTo) {
        chipsValues.iccidRange += ` ${t('common.to')}: ${iccidTo}`;
      }
    }

    Object.entries(othersFilters).forEach(([key, value]) => {
      if (key === 'chosenIdType' || key === 'searchText' || key === 'mnoNames') {
        return;
      }

      if (value !== '') {
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          value,
        );
      }
    });

    return chipsValues;
  };

  const filtersChips = createValuesForChips(otherFilters);

  return (
    <FiltersChips
      externalChips={
        <>
          <TagsChips tagsAtom={tagsFilterAtom} />
          <SubscriptionStatusChips subscriptionStatusAtom={subscriptionStatusFilterAtom} />
          <AccountChips accountAtom={accountsFilterAtom} />
          <ProductCodesChips productCodesAtom={productCodesFilterAtom} />
          <MnosChips
            mnoAtom={mobileNetworkOperatorFilterAtom}
            mnoNamesFilterAtom={mnoNamesFilterAtom}
            mnoIdFilterAtom={mobileNetworkOperatorIdFilterAtom}
          />
          <CustomFieldsChips customFieldsAtom={customFieldsFilterAtom} />
          <MnoStatusChips mnoStatusAtom={mnoStatusFilterAtom} />
        </>
      }
      filters={filtersChips}
      onFilterClear={onFilterClear}
      onFiltersClear={clearAll}
    />
  );
};
